import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { assets } from '../constants/theme';

const timeline = [
  {
    year: 'Phase 1',
    status: 'Foundation',
    color: '#D96D29',
    milestones: [
      'Establish enhanced online presence',
      'Build strong wholesale partnerships',
      'Outline franchise expansion strategy',
      'Initiate community training programs',
    ],
  },
  {
    year: 'Phase 2',
    status: 'Initial Growth',
    color: '#A63C1E',
    milestones: [
      'Scale wholesale distribution network',
      'Launch initial franchise locations',
      'Expand into new international markets',
      'Grow our dedicated team',
      'Achieve sustainable revenue growth',
    ],
  },
  {
    year: 'Phase 3',
    status: 'Expansion',
    color: '#8B4224',
    milestones: [
      'Establish a robust global wholesale presence',
      'Expand franchise footprint significantly',
      'Enter major European markets',
      'Create substantial employment opportunities',
      'Accelerate financial growth',
    ],
  },
  {
    year: 'Future',
    status: 'Long-Term',
    color: '#4A2010',
    milestones: [
      'Maximize wholesale and franchise reach',
      'Achieve comprehensive global market presence',
      'Generate widespread sustainable jobs',
      'Ensure long-term financial stability',
      'Become a recognized global movement',
    ],
  },
];

const goals = [
  {
    id: 'market',
    icon: '🌍',
    title: 'Global Market Expansion',
    timeline: 'Strategic Focus',
    color: '#1E5FA0',
    desc: 'Beginning with the international wholesale market, Savana Sips is systematically building a global distribution network. From specialty cafes to hospitality partners, we\'re putting Kenyan specialty coffee in the hands of those who appreciate it most.',
    channels: ['E-Commerce (Clover + Own Platform)', 'Retail Partners', 'Direct Wholesale', 'B2B Hospitality'],
  },
  {
    id: 'community',
    icon: '🤝',
    title: 'Community Empowerment',
    timeline: 'Core Mission',
    color: '#2D6A2E',
    desc: 'Every Savana Sips franchise represents an opportunity for an African entrepreneur to build their own coffee business with full support, training, and brand backing. We\'re lowering the barrier to entry so that community members can own a piece of the global coffee economy.',
    channels: ['Franchise Opportunities', 'Coffee Farming Training', 'Business Skills Development', 'Women Empowerment Programs'],
  },
  {
    id: 'partnerships',
    icon: '🌐',
    title: 'International Partnerships',
    timeline: 'Collaborative Growth',
    color: '#8B4224',
    desc: 'Building a truly global coffee movement requires collaborative partnerships. We\'re actively cultivating relationships with NGOs, fair trade organizations, and specialty coffee associations to create a network that amplifies our community impact and builds market credibility.',
    channels: ['Coffee Industry Associations', 'Fair Trade Organizations', 'Impact Investment Partners', 'Agricultural Ministries'],
  },
  {
    id: 'employment',
    icon: '💼',
    title: 'Employment Creation',
    timeline: 'Long-Term Impact',
    color: '#1C1209',
    desc: 'Jobs are the most direct form of community development. We\'re creating meaningful employment at every stage of the coffee value chain — from seasonal harvesters and processing station workers to baristas, roastery staff, and corporate team members.',
    channels: ['Coffee Farm Management', 'Processing & Roasting', 'Café & Hospitality', 'Corporate & Support'],
  },
];

function GoalCard({ goal }) {
  const s = {
    card: {
      backgroundColor: '#fff',
      border: '1px solid #EDE5DC',
      overflow: 'hidden',
    },
    header: {
      padding: '2rem 2rem 1.5rem',
      borderBottom: '3px solid',
      borderColor: goal.color,
    },
    iconRow: {
      display: 'flex', alignItems: 'center',
      gap: '1rem', marginBottom: '0.75rem',
    },
    icon: { fontSize: '2.25rem' },
    meta: { display: 'flex', flexDirection: 'column' },
    timeline: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: goal.color,
    },
    title: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.65rem', color: '#1C1209', lineHeight: 1.1,
    },
    body: { padding: '1.75rem 2rem 2rem' },
    desc: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.9rem', color: '#3A2010', lineHeight: 1.75,
      marginBottom: '2rem',
    },
    channelsTitle: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: '#8B5E3C', marginBottom: '0.75rem',
    },
    channelsList: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
    channel: {
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.85rem', color: '#3A2010',
    },
    dot: {
      width: '6px', height: '6px', borderRadius: '50%',
      backgroundColor: goal.color, flexShrink: 0,
    },
  };

  return (
    <div style={s.card}>
      <div style={s.header}>
        <div style={s.iconRow}>
          <div style={s.icon}>{goal.icon}</div>
          <div style={s.meta}>
            <div style={s.timeline}>{goal.timeline}</div>
            <h3 style={s.title}>{goal.title}</h3>
          </div>
        </div>
      </div>
      <div style={s.body}>
        <p style={s.desc}>{goal.desc}</p>

        <div style={s.channelsTitle}>Focus Areas</div>
        <div style={s.channelsList}>
          {goal.channels.map(ch => (
            <div key={ch} style={s.channel}>
              <div style={s.dot} />
              {ch}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function VisionPage() {
  useEffect(() => {
    document.title = 'Our Vision | Future & Goals | Savana Sips';
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fadeIn');
      });
    }, { threshold: 0.04 });

    document.querySelectorAll('.vp-fade').forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.08}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const s = {
    page: { backgroundColor: '#FAF7F2', paddingTop: '100px' },
    pageHero: {
      position: 'relative', padding: '7rem 6% 6rem',
      backgroundColor: '#1C1007', overflow: 'hidden',
    },
    heroBg: {
      position: 'absolute', inset: 0,
      backgroundImage: `url('${assets.imgVision}')`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      opacity: 0.25,
    },
    heroContent: { position: 'relative', zIndex: 1, maxWidth: '800px' },
    breadcrumb: {
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      marginBottom: '2rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', color: 'rgba(250,247,242,0.5)',
    },
    label: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', fontWeight: 700,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      color: '#D96D29', marginBottom: '1rem',
    },
    pageTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(3rem, 7vw, 5.5rem)',
      color: '#FAF7F2', lineHeight: 1.05, marginBottom: '1.5rem',
    },
    accentText: { fontStyle: 'italic', color: '#D96D29' },
    pageSubtitle: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1.05rem', color: 'rgba(250,247,242,0.75)',
      lineHeight: 1.8, maxWidth: '600px', marginBottom: '3rem',
    },
    visionStatement: {
      padding: '7rem 6%',
      backgroundColor: '#fff',
    },
    visionInner: {
      maxWidth: '900px', margin: '0 auto',
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      gap: '5rem', alignItems: 'center',
    },
    visionImg: { width: '100%', aspectRatio: '3/4', objectFit: 'cover' },
    visionText: {},
    visionLabel: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', fontWeight: 700,
      letterSpacing: '0.15em', textTransform: 'uppercase',
      color: '#D96D29', marginBottom: '1rem',
    },
    visionTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
      color: '#1C1209', lineHeight: 1.15, marginBottom: '1.5rem',
    },
    visionBody: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1rem', color: '#3A2010', lineHeight: 1.8,
      marginBottom: '1.25rem',
    },
    pillarsSection: {
      padding: '7rem 6%',
      backgroundColor: '#1C1007',
    },
    pillarsInner: { maxWidth: '1240px', margin: '0 auto' },
    pillarsHeader: { textAlign: 'center', marginBottom: '4rem' },
    pillarsTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      color: '#FAF7F2', marginBottom: '0.75rem',
    },
    pillarsSub: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.95rem', color: 'rgba(250,247,242,0.6)',
      maxWidth: '480px', margin: '0 auto',
    },
    pillarsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1px',
      backgroundColor: 'rgba(250,247,242,0.06)',
    },
    pillar: {
      padding: '3rem 2rem',
      backgroundColor: '#1C1007',
      textAlign: 'center',
    },
    pillarIcon: { fontSize: '2.5rem', marginBottom: '1rem' },
    pillarTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.5rem', color: '#FAF7F2', marginBottom: '0.75rem',
    },
    pillarDesc: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.85rem', color: 'rgba(250,247,242,0.6)', lineHeight: 1.7,
    },
    goalsSection: {
      padding: '7rem 6%', backgroundColor: '#FAF7F2',
    },
    goalsInner: { maxWidth: '1240px', margin: '0 auto' },
    goalsHeader: { textAlign: 'center', marginBottom: '4rem' },
    goalsTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      color: '#1C1209', marginBottom: '0.75rem',
    },
    goalsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '2rem',
    },
    timelineSection: {
      padding: '7rem 6%', backgroundColor: '#2B1509',
    },
    timelineInner: { maxWidth: '900px', margin: '0 auto' },
    timelineHeader: { textAlign: 'center', marginBottom: '4rem', color: '#FAF7F2' },
    timelineTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      color: '#FAF7F2',
    },
    timelineGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2px',
    },
    timelineCard: (tl) => ({
      backgroundColor: '#1C1007',
      padding: '2rem',
      borderTop: `4px solid ${tl.color}`,
    }),
    tlYear: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '2.5rem', lineHeight: 1,
    },
    tlStatus: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', marginBottom: '1.25rem',
    },
    tlMilestone: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.82rem', color: 'rgba(250,247,242,0.7)',
      lineHeight: 1.6, marginBottom: '0.4rem',
    },
    ctaSection: {
      padding: '7rem 6%', textAlign: 'center',
      backgroundColor: '#FAF7F2',
    },
    ctaTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      color: '#1C1209', marginBottom: '1rem',
    },
    ctaBody: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1rem', color: '#4A2C1A', lineHeight: 1.7,
      maxWidth: '500px', margin: '0 auto 3rem',
    },
    ctaRow: { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' },
  };

  const pillars = [
    { icon: '🏆', title: 'Quality', desc: 'Specialty grade, small-batch, precision roasting at every step.' },
    { icon: '♻️', title: 'Sustainability', desc: 'Direct trade, fair prices, environmental responsibility throughout.' },
    { icon: '🤝', title: 'Community', desc: 'Farmer partnerships, employment creation, franchise opportunities.' },
    { icon: '☕', title: 'Experience', desc: 'Your perfect cup ritual, from farm to your morning moment.' },
  ];

  return (
    <div style={s.page} className="page-enter">
      {/* Page Hero */}
      <div style={s.pageHero}>
        <div style={s.heroBg} aria-hidden="true" />
        <div style={s.heroContent}>
          <div style={s.breadcrumb}>
            <Link to="/" style={{ color: 'rgba(250,247,242,0.45)', textDecoration: 'none' }}>Home</Link>
            <span>›</span>
            <span>Our Vision</span>
          </div>
          <div style={s.label}>Our Vision</div>
          <h1 style={s.pageTitle}>
            Building a Global<br />
            <span style={s.accentText}>Coffee Movement</span>
          </h1>
          <p style={s.pageSubtitle}>
            At Savana Sips, we believe coffee is more than a beverage — it's a bridge between
            communities, cultures, and possibilities. Join our movement.
          </p>
          <Link to="/products" className="btn-primary">
            Start Your Journey →
          </Link>
        </div>
      </div>

      {/* Vision Statement */}
      <div style={s.visionStatement}>
        <div style={{ ...s.visionInner, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <img src={assets.imgVision} alt="Savana Sips community" style={s.visionImg} loading="lazy" />
          <div style={s.visionText}>
            <div style={s.visionLabel}>Our Mission Statement</div>
            <h2 style={s.visionTitle}>Every Cup. Every Day. Every Community.</h2>
            <p style={s.visionBody}>
              At Savana Sips, we believe coffee is more than a beverage — it's a bridge between
              communities, cultures, and possibilities. Our mission is to empower African coffee
              farmers and communities, create sustainable income opportunities, and build a global
              coffee ecosystem where everyone thrives.
            </p>
            <p style={s.visionBody}>
              Every cup you drink supports a farmer's family, builds a community's future, and
              grows our movement. From the volcanic slopes of Mt. Kenya to your morning cup,
              every step of our journey is intentional, ethical, and community-first.
            </p>
            <p style={{ ...s.visionBody, fontStyle: 'italic', color: '#A0693A', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.15rem' }}>
              "Your Cup! Your Day! At Savana Sips — it's all about your coffee cup, your day."
            </p>
          </div>
        </div>
      </div>

      {/* Brand Pillars */}
      <div style={s.pillarsSection}>
        <div style={s.pillarsInner}>
          <div className="vp-fade preFade" style={s.pillarsHeader}>
            <div style={{ ...s.label, color: '#D96D29' }}>Our Pillars</div>
            <h2 style={s.pillarsTitle}>What We Stand For</h2>
            <p style={s.pillarsSub}>Four principles that guide every decision we make.</p>
          </div>
          <div style={s.pillarsGrid}>
            {pillars.map(p => (
              <div key={p.title} className="vp-fade preFade" style={s.pillar}>
                <div style={s.pillarIcon}>{p.icon}</div>
                <h3 style={s.pillarTitle}>{p.title}</h3>
                <p style={s.pillarDesc}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strategic Goals */}
      <div style={s.goalsSection}>
        <div style={s.goalsInner}>
          <div className="vp-fade preFade" style={s.goalsHeader}>
            <div className="section-label">Strategic Goals</div>
            <h2 style={s.goalsTitle}>Our Path Forward</h2>
          </div>
          <div className="vision-goals-grid vp-fade preFade" style={{ ...s.goalsGrid, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {goals.map(goal => <GoalCard key={goal.id} goal={goal} />)}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div style={s.timelineSection}>
        <div style={s.timelineInner}>
          <div className="vp-fade preFade" style={s.timelineHeader}>
            <div style={{ ...s.label, color: '#D96D29' }}>Milestones</div>
            <h2 style={s.timelineTitle}>The Road Ahead</h2>
          </div>
          <div style={s.timelineGrid}>
            {timeline.map((tl) => (
              <div key={tl.year} className="vp-fade preFade" style={s.timelineCard(tl)}>
                <div style={{ ...s.tlYear, color: tl.color }}>{tl.year}</div>
                <div style={{ ...s.tlStatus, color: tl.color }}>{tl.status}</div>
                {tl.milestones.map((m, i) => (
                  <div key={i} style={s.tlMilestone}>{m}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={s.ctaSection}>
        <div className="vp-fade preFade">
          <h2 style={s.ctaTitle}>Join Our Movement</h2>
          <p style={s.ctaBody}>
            Together, we're creating sustainable futures through coffee and community empowerment.
            Every cup is a step toward a better world.
          </p>
          <div style={s.ctaRow}>
            <Link to="/products" className="btn-primary">Shop Our Coffee →</Link>
            <Link to="/contact" className="btn-secondary">Partner With Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
